/**
 * Short notices that confirm an action ("Saved") or report a failure, at
 * the screen's corner, announced to screen readers (Radix Toast). Every
 * action gives feedback (CLAUDE.md section 7). Notify with
 * `const notify = useNotify(); notify({ title: "Saved" })`.
 */
import * as ToastPrimitive from "@radix-ui/react-toast";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { type ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { type Notice, type Notify, NotifyContext, type Tone } from "./use-notify";

const ICONS: Record<Tone, { icon: typeof Info; colour: string }> = {
  success: { icon: CheckCircle2, colour: "text-success" },
  error: { icon: XCircle, colour: "text-error" },
  warning: { icon: AlertTriangle, colour: "text-warning" },
  info: { icon: Info, colour: "text-info" },
};

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [notices, setNotices] = useState<(Notice & { id: number })[]>([]);
  const notify = useCallback<Notify>((notice) => {
    setNotices((current) => [...current.slice(-2), { ...notice, id: Date.now() + Math.random() }]);
  }, []);
  return (
    <NotifyContext.Provider value={notify}>
      <ToastPrimitive.Provider swipeDirection="right" label={t("ui.notifications")}>
        {children}
        {notices.map(({ id, title, description, tone = "success" }) => {
          const { icon: Icon, colour } = ICONS[tone];
          return (
            <ToastPrimitive.Root
              key={id}
              type={tone === "error" ? "foreground" : "background"}
              duration={tone === "error" ? 8000 : 4000}
              onOpenChange={(open) => { if (!open) setNotices((current) => current.filter((item) => item.id !== id)); }}
              className="flex items-start gap-3 rounded-lg border border-border bg-popover p-4 text-foreground shadow-lg data-[state=open]:animate-slide-in-right data-[state=closed]:animate-fade-out"
            >
              <Icon aria-hidden="true" className={cn("mt-0.5 size-5 shrink-0", colour)} />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <ToastPrimitive.Title className="text-sm font-semibold">{title}</ToastPrimitive.Title>
                {description ? <ToastPrimitive.Description className="text-sm text-muted-foreground">{description}</ToastPrimitive.Description> : null}
              </div>
              <ToastPrimitive.Close aria-label={t("ui.dismiss")} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
                <X aria-hidden="true" className="size-4" />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}
        <ToastPrimitive.Viewport className="fixed bottom-0 right-0 z-[60] flex w-full max-w-sm flex-col gap-2 p-4 outline-none" />
      </ToastPrimitive.Provider>
    </NotifyContext.Provider>
  );
} // prettier-ignore
